import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  HeadContent,
  Link,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import { LazyMotion, domAnimation } from "framer-motion";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ChatWidget } from "@/components/ChatWidget";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { reportVibeError } from "@/lib/vibe-error-reporting";
import { initLeadClickTracking } from "@/lib/analytics";
import appCss from "../styles.css?url";

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function ScrollToTop() {
  const router = useRouter();
  const pathname = router.state.location.pathname;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function DeferredToasters() {
  const [load, setLoad] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setLoad(true), 4000);
    const handleInteraction = () => {
      window.sessionStorage.setItem("userInteracted", "true");
      setLoad(true);
    };

    window.addEventListener("scroll", handleInteraction, {
      once: true,
      passive: true,
    });
    window.addEventListener("touchstart", handleInteraction, {
      once: true,
      passive: true,
    });
    window.addEventListener("mousemove", handleInteraction, {
      once: true,
      passive: true,
    });

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
      window.removeEventListener("mousemove", handleInteraction);
    };
  }, []);

  return load ? (
    <>
      <Toaster />
      <Sonner />
    </>
  ) : null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    return initLeadClickTracking();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <LazyMotion features={domAnimation}>
        <DeferredToasters />
        <ScrollToTop />
        <div className="min-h-screen bg-background text-foreground flex flex-col">
          <Header />
          <main className="flex-1">
            <Outlet />
          </main>
          <Footer />
          <ChatWidget />
        </div>
      </LazyMotion>
    </QueryClientProvider>
  );
}

function RootErrorComponent({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  const router = useRouter();

  useEffect(() => {
    console.error(error);
    reportVibeError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <main className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
      <section className="max-w-lg text-center space-y-6">
        <p className="text-sm font-semibold tracking-[0.18em] uppercase text-secondary">
          Something went wrong
        </p>
        <h1 className="text-4xl font-bold">This page didn&apos;t load</h1>
        <p className="text-muted-foreground">
          Please try again or return home to continue exploring Precision Pavers
          &amp; Pool Design.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <button
            type="button"
            className="rounded-md bg-primary px-5 py-3 font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <Link
            to="/"
            className="rounded-md border border-border px-5 py-3 font-semibold transition-colors hover:bg-muted"
          >
            Go home
          </Link>
        </div>
      </section>
    </main>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    head: () => ({
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1.0" },
        { title: "Pool Remodeling & Pavers in Broward | Precision Pavers" },
        {
          name: "description",
          content:
            "Premium pool remodeling, deck resurfacing, driveway pavers, artificial turf, and outdoor enhancements across South Florida. Family-owned. Free estimates.",
        },
        { name: "author", content: "Precision Pavers & Pool Design" },
        {
          name: "google-site-verification",
          content: "VhhDClzcncbCMn4mJkDBZYxqyoW0SJP63DREjnCaMVU",
        },
        {
          property: "og:title",
          content: "Pool Remodeling & Pavers in Broward | Precision Pavers",
        },
        {
          property: "og:description",
          content:
            "Premium pool remodeling, deck resurfacing, driveway pavers, artificial turf, and outdoor enhancements across South Florida. Family-owned. Free estimates.",
        },
        { property: "og:type", content: "website" },
        {
          property: "og:url",
          content: "https://precisionpaverspools.com/",
        },
        { name: "twitter:card", content: "summary_large_image" },
        {
          name: "twitter:title",
          content: "Pool Remodeling & Pavers in Broward | Precision Pavers",
        },
        {
          name: "twitter:description",
          content:
            "Premium pool remodeling, deck resurfacing, driveway pavers, artificial turf, and outdoor enhancements across South Florida. Family-owned. Free estimates.",
        },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
        { rel: "preconnect", href: "https://vibe.filesafe.space" },
        {
          rel: "preconnect",
          href: "https://www.googletagmanager.com",
        },
        {
          rel: "preload",
          as: "image",
          media: "(max-width: 768px)",
          href: "https://vibe.filesafe.space/1779750798598342338/attachments/f0ebb8fb-32ad-4900-85cf-381a9d838097.webp",
          fetchPriority: "high",
        },
      ],
      scripts: [
        {
          src: "https://www.googletagmanager.com/gtag/js?id=G-DE8R7HM8WV",
          async: true,
        },
        {
          children:
            "window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}window.gtag = gtag;gtag('js', new Date());gtag('config', 'G-DE8R7HM8WV', { send_page_view: true });",
        },
      ],
    }),
    shellComponent: RootShell,
    component: RootComponent,
    notFoundComponent: () => (
      <main className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
        <section className="max-w-lg text-center space-y-6">
          <p className="text-sm font-semibold tracking-[0.18em] uppercase text-secondary">
            404
          </p>
          <h1 className="text-4xl font-bold">Page not found</h1>
          <p className="text-muted-foreground">
            The page you requested could not be found.
          </p>
          <Link
            to="/"
            className="inline-flex rounded-md bg-primary px-5 py-3 font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            Go home
          </Link>
        </section>
      </main>
    ),
    errorComponent: RootErrorComponent,
  },
);
